import React from "react";
import { useSelector } from "react-redux";
import { Field } from "redux-form";

import { ReduxControls, ProgressIndicator } from "rhombus-react";

import { formatBytes, noop } from "utils/common";
import { ASSET_TYPES, SOCIAL_AD_TYPES } from "utils/constants";
import { CREATIVE_NAME_MAX_LENGTH } from "widgets/forms/validators/Creatives";
import { FACEBOOK_IMAGE_CONSTRAINTS } from "social/validators/facebook";
import { FB_CTA_OPTIONS } from "social/constants/CTA";

import { buildFacebookPages } from "selectors/Social";

import {
	formatAPIOption,
	mapFacebookStringToOption,
	mapStringToOption,
	normalizeAPIOption,
	normalizeOptionToString
} from "social/utils";
import { useSocialFields, useDefaultAccounts } from "social/formats/utils";

import AssetSelect from "social/AssetSelect";

const {
	MAX_IMAGE_BYTES,
	IMAGE_ACCEPT,
	MAX_LENGTH_LANDING_PAGE,
} = FACEBOOK_IMAGE_CONSTRAINTS;

const FILE_SIZE_REQUIREMENT = { name: "File Size:", description: `${formatBytes(MAX_IMAGE_BYTES)} Maximum` };
const FILE_TYPE_REQUIREMENT = { name: "File Types:", description: IMAGE_ACCEPT };

const facebookPagesTextProps = {
	component: ReduxControls.Input,
	normalize: mapFacebookStringToOption,
	format: normalizeOptionToString,
	placeholder: "Enter Facebook Page id",
};

const instagramTextProps = {
	component: ReduxControls.Input,
	normalize: mapFacebookStringToOption,
	format: normalizeOptionToString,
	placeholder: "Enter Instagram account id",
};

export default function FacebookImageDefault(props){
	const {
		libraryId,
		isEditMode,
		navigationActions,
	} = props;

	const isLoading = useSelector(state => state.social?.facebookPages?.isLoading);
	const { facebookPages, instagramAccounts } = useSelector(buildFacebookPages);

	useDefaultAccounts({
		libraryId,
		isEditMode,
		adType: SOCIAL_AD_TYPES.FACEBOOK_IMAGE,
	});

	const facebookPagesSelectProps = {
		component: ReduxControls.Select,
		normalize: normalizeAPIOption,
		format: formatAPIOption,
		placeholder: "Select...",
		options: facebookPages,
		menuPlacement: "top",
		onMenuScrollToBottom: noop,
	};

	const facebookPagesProps = facebookPages.length ? facebookPagesSelectProps : facebookPagesTextProps;

	const instagramSelectProps = {
		placeholder: "Select...",
		component: ReduxControls.Select,
		normalize: normalizeAPIOption,
		format: formatAPIOption,
		options: instagramAccounts,
		menuPlacement: "top",
		onMenuScrollToBottom: noop,
	};

	const instagramProps = instagramAccounts.length ? instagramSelectProps : instagramTextProps;

	const baseFields = {
		image: {
			name: "image",
			label: "Image",
			className: "full-width",
			assetType: ASSET_TYPES.IMAGE,
			component: AssetSelect,
			requirements: [
				FILE_SIZE_REQUIREMENT,
				FILE_TYPE_REQUIREMENT,
				{ name: "Dimensions:", description: "254x133 pixels or larger required, 1080x1080 or larger recommended" }
			],
			required: true,
			accept: IMAGE_ACCEPT,
			tip: {
				direction: "top",
				description: "At least 600x600 for better placement compatibility",
			},
			libraryId,
			navigationActions,
		},
		name: {
			name: "name",
			label: "Creative Name",
			className: "full-width",
			placeholder: "Enter a creative name",
			component: ReduxControls.Input,
			charMax: CREATIVE_NAME_MAX_LENGTH,
			showCharCount: true,
			showPristineErrors: true,
			required: true,
		},
		headline: {
			name: "headline",
			label: "Headline",
			className: "full-width",
			placeholder: "Enter a short headline",
			tip: {
				description: "May get truncated after 40 characters",
				direction: "right",
			},
			component: ReduxControls.Input,
		},
		primaryText: {
			name: "primaryText",
			label: "Primary Text",
			className: "full-width",
			placeholder: "Tell people what your creative is about",
			tip: {
				description: "May get truncated after 125 characters",
				direction: "right",
			},
			component: ReduxControls.Input,
			type: "textarea",
		},
		description: {
			name: "description",
			label: "Description",
			className: "full-width",
			placeholder: "Include additional details",
			tip: {
				description: "May get truncated after 30 characters",
				direction: "right",
			},
			component: ReduxControls.Input,
		},
		landingPage: {
			name: "landingPage",
			label: "Landing Page",
			className: "full-width",
			placeholder: "https://www.example.com",
			component: ReduxControls.Input,
			required: true,
			charMax: MAX_LENGTH_LANDING_PAGE,
			showCharCount: true,
		},
		displayLink: {
			name: "displayLink",
			label: "Display Link",
			className: "half-width",
			placeholder: "Enter link text to be displayed",
			component: ReduxControls.Input,
		},
		cta: {
			name: "cta",
			label: "CTA",
			className: "half-width",
			placeholder: "Enter Call to Action Text",
			component: ReduxControls.Select,
			options: FB_CTA_OPTIONS,
			normalize: normalizeOptionToString,
			format: mapStringToOption,
			isClearable: false,
			menuPlacement: "top",
			required: true,
			onMenuScrollToBottom: noop,
		},
		facebookPage: {
			name: "facebookPage",
			label: "Facebook Page",
			className: "half-width",
			required: true,
			...facebookPagesProps,
		},
		instagramAccount: {
			name: "instagramAccount",
			label: "Instagram Account",
			className: "half-width",
			...instagramProps,
		},
	};

	const fieldProps = useSocialFields(baseFields, props);

	return (
		<>
			<ProgressIndicator isLoading={isLoading} />
			<div className="fields">
				{fieldProps.map((properties) => {
					return <Field key={properties.name} {...properties} />;
				})}
			</div>
		</>
	);
}

FacebookImageDefault.displayName = "FacebookImageDefaultPlacement";
